import React, { Component } from 'react';
import maplibregl, { AttributionControl } from 'maplibre-gl';
import * as turf from '@turf/turf';
import polyline from '@mapbox/polyline';
import Polyline from './polyline';
import TrackingMap from '../TrackingMap';
import defaultSourceMarker from '../TrackingMap/source.svg';
import defaultDestinationMarker from '../TrackingMap/destination.svg';
import defaultCarLogo from '../LiveTrackingContainer/car.svg';
import defaultDestinationReachedMarker from '../TrackingMap/destination-reached.svg';
import somethingWrongImage from '../Error/something-wrong.svg';
import { sendBaseMapLoadedFailureEvent, sendBaseMapLoadedSuccessEvent } from '../../googleAnalytics';
import 'maplibre-gl/dist/maplibre-gl.css';
import './style.scss';
import { ClickstreamContext } from '../../providers/Clickstream.provider';

export default class GojekMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mapLoading: true,
      mapLoaded: false,
      mapError: false,
    };
    this.mapContainerRef = React.createRef();
  }
  map = null;
  driverMarker = null;
  sourceMarker = null;
  destinationMarker = null;
  startTime = null;
  mapName = "gojek";

  static contextType = ClickstreamContext;

  componentDidMount() {
    this.setState({ mapLoading: true, mapLoaded: false, mapError: false });
    this.startTime = performance.now();
    const centerLongitude = this.props.points?.[0]?.longitude ?? 0;
    const centerLatitude = this.props.points?.[0]?.latitude ?? 0;
    this.map = new maplibregl.Map({
      container: this.mapContainerRef.current,
      style: process.env.REACT_APP_GOJEK_MAPS_URL,
      center: [centerLongitude, centerLatitude],
      zoom: 14,
      maxZoom: 18,
      attributionControl: false
    });

    const clickstream = this.context;

    this.map.on('load', () => {
      const timeTaken = performance.now() - this.startTime;
      this.trackMapLoadSuccess(clickstream, timeTaken);

      this.setState({ mapLoading: false, mapLoaded: true, mapError: false });

      this.renderDriverMarker();

      this.fitMapBounds(this.props.points);

      this.sourceMarker = new maplibregl.Marker({ element: this.createCustomMarkerElement(this.getSourceMarker()), anchor: 'bottom' })
        .setLngLat([this.props.source.longitude, this.props.source.latitude])
        .addTo(this.map);

      this.destinationMarker = new maplibregl.Marker({ element: this.createCustomMarkerElement(this.getDestinationMarker()), anchor: 'bottom' })
        .setLngLat([this.props.destination.longitude, this.props.destination.latitude])
        .addTo(this.map);

      this.map.on('dragend', () => this.props.onBoundsChange(false));

      this.map.addControl(new AttributionControl({
        compact: true
      }), 'bottom-left');
      if (!this.props.isDesktopView) {
        this.adjustAttributionControlPadding();
      }

      const nav = new maplibregl.NavigationControl({
        showCompass: false
      });
      if(this.props.isDesktopView) {
        this.map.addControl(nav, 'bottom-right');
      } else {
        this.map.removeControl(nav);
      }
    });

    this.map.on('error', (e) => {
      this.trackMapLoadFailure(clickstream, e.error);
      this.setState({ mapLoading: false, mapLoaded: false, mapError: true });
    });
  }

  componentDidUpdate() {
    if (this.props.autoMapFocus) {
      this.fitMapBounds(this.props.points);
    }
    if (this.driverMarker != null && this.props.driver) {
      let oldLngLat = this.driverMarker.getLngLat();
      this.animateMarker(oldLngLat, [this.props.driver.longitude, this.props.driver.latitude], 6000);
    } else {
      this.renderDriverMarker();
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
    }
  }

  renderDriverMarker() {
    if (this.state.mapLoaded && this.props.driver && this.driverMarker == null) {
      this.driverMarker = new maplibregl.Marker({ element: this.createCustomMarkerElement(this.getDriverMarkerIcon()) })
        .setLngLat([this.props.driver.longitude, this.props.driver.latitude])
        .setSubpixelPositioning(true)
        .addTo(this.map);
    } else if (this.props.driver == null && this.driverMarker) {
      this.driverMarker.remove();
    }
  }

  fitMapBounds(points) {
    const bounds = new maplibregl.LngLatBounds(); 
    points.forEach((point) => {
      bounds.extend([point.longitude, point.latitude]);
    });
    this.map.fitBounds(bounds, { padding: this.props.padding, duration: 500 });
  }

  adjustAttributionControlPadding() {
    const bottomLeftControls = document.querySelector('.maplibregl-ctrl-bottom-left');
    if (bottomLeftControls) {
      bottomLeftControls.style.paddingBottom = `${this.props.padding.bottom - 25}px`;
      bottomLeftControls.style.zIndex = '1';
    }
  };

  createCustomMarkerElement = (image) => {
    const el = document.createElement('div');
    el.style.backgroundImage = `url(${image})`;
    el.style.width = '50px';
    el.style.height = '50px';
    el.style.backgroundSize = 'contain';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.backgroundPosition = 'center';
    return el;
  };

  getSourceMarker() {
    let sourceMarker = TrackingMap.serviceTypeSourceMarkerMap[this.props.serviceType];
    return sourceMarker ? sourceMarker : defaultSourceMarker;
  }

  getDestinationMarker() {
    let destinationMarker = TrackingMap.serviceTypeDestinationMarkerMap[this.props.serviceType];
    destinationMarker = destinationMarker ? destinationMarker : defaultDestinationMarker;
    return this.props.bookingComplete ? this.getDestinationReachedMarker() : destinationMarker;
  }

  getDestinationReachedMarker() {
    let destinationReachedMarker = TrackingMap.serviceTypeDestinationReachedMarkerMap[this.props.serviceType];
    return destinationReachedMarker ? destinationReachedMarker : defaultDestinationReachedMarker;
  }

  getDriverMarkerIcon() {
    return this.props.driverMarkerIcon || defaultCarLogo;
  }

  getPolylinePath() {
    const encoded = this.props.route.dynamic.polylines[0];
    try {
      const decoded = polyline.decode(encoded);
      return decoded.map(coord => [coord[1], coord[0]]);
    } catch (error) {
      console.error('Error decoding polyline:', error);
      return [];
    }
  }

  animateMarker = (oldLngLat, newLngLat, duration) => {
    let start;
    const startLng = oldLngLat.lng;
    const startLat = oldLngLat.lat;
    const [endLng, endLat] = newLngLat;
  
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
  
      if (progress < 1) {
        const currentLng = startLng + (endLng - startLng) * progress;
        const currentLat = startLat + (endLat - startLat) * progress;
  
        if (
          Math.abs(currentLng - startLng) > 0.0001 ||
          Math.abs(currentLat - startLat) > 0.0001
        ) {
          this.driverMarker.setLngLat([currentLng, currentLat]);
  
          const bearing = turf.bearing(
            turf.point([startLng, startLat]),
            turf.point([endLng, endLat])
          );
  
          const roundedBearing = Math.round(bearing * 10) / 10;
          this.driverMarker.setRotation(roundedBearing);
        }
  
        requestAnimationFrame(animate);
      } else {
        this.driverMarker.setLngLat([endLng, endLat]);
      }
    };
  
    requestAnimationFrame(animate);
  };  

  trackMapLoadSuccess(clickstream, timeTaken) {
    sendBaseMapLoadedSuccessEvent(this.props.token, this.mapName, timeTaken);

    if (clickstream) {
      clickstream.track({
        eventName: "Map Load",
        baseMapLoad: {
          status: "success",
          map: this.mapName,
          tripId: this.props.token,
          loadTime: `${Math.round(timeTaken)}`,
        }
      });
    }
  }

  trackMapLoadFailure(clickstream, error) {
    sendBaseMapLoadedFailureEvent(this.props.token, this.mapName, error);
    if (clickstream) {
      clickstream.track({
        eventName: "Map Load",
        baseMapLoad: {
          status: "failed",
          map: this.mapName,
          tripId: this.props.token,
          errorMessage: `${error}`,
        }
      });
    }
  }

  render() {
    const { mapLoading, mapLoaded, mapError } = this.state;

    return (
      <div
        ref={this.mapContainerRef}
        style={{ width: '100%', height: '100%' }}
      >
        { mapLoading
          && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
              <div className="circular-loader" />
            </div>
          )
        }
        { mapLoaded 
          && this.props.route 
          && this.props.route.dynamic.polylines.length > 0
          && (
            <Polyline 
              driverLocation={this.props.driver} 
              route={this.props.route} 
              snappedSegment={this.props.snappedSegment}
              map={this.map}
            />
          )
        }
        { mapError && (
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            justifyContent: "center", 
            alignItems: "center", 
            height: "100vh", 
            backgroundColor: "#f0f0f0"
          }}>
            <img
              src={somethingWrongImage}
              alt="Error" 
            />
            <h2 style={{
              fontSize: "1.5rem",
              color: "#333",
              marginBottom: "10px"
            }}>
              Oops! Something went wrong.
            </h2>
            <p style={{
              fontSize: "1rem",
              color: "#777"
            }}>
              This page didn’t load Maps correctly. Please refresh again.
            </p>
          </div>
        )}
      </div>
    );
  }
}
